import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ModalComponent from "../modal";
import InputComponent from "../input";
import ButtonComponent from "../button";
import { useFormik } from "formik";
import { apiConfigurationSchema } from "../../helpers/schemas";
import TextareaComponent from "../textarea";
import { useLocation } from "react-router";
import { fieldTypesConstant } from "../../constants/app-constants";
import { toast } from "react-toastify";
import IconButtonComponent from "../icon-button";
import { ListSettingSvg, PlusSvg, VerticalDotsSvg } from "../../assets/svgs";
import StepsProgressBarComponent from "../steps-progress-bar";
import EmptyModalComponent from "../empty-modal";
import { AuthPageHead } from "../../styles/global-styles";
import { ActionsContainer, Form, FormError, InstitutionSection, Label, TableWrapper } from "./add-update-api-configuration-modal.style";
import TableComponent from "../table";
import DropDownComponent from "../dropdown";
import AddUpdateApiConfigurationFieldsModal from "../add-update-api-configuration-fields-modal";

const AddUpdateApiConfigurationModal = ({ data, close, label, institution, handleFormSubmit, update, viewOnly, fromMakerChecker ,slide}) => {
    const location = useLocation();
    const isChecker = location.pathname.includes('checker');
    const { apiConfigOtpValidityOptions } = useSelector((state) => state.apiConfigReducer);

    const isDisabledAll = isChecker || viewOnly;

    const initialForm = {
        apiName: '',
        apiType: '',
        attributeField: fieldTypesConstant?.dropdown,
        attributeName: '',
        isDatetimeField: false,
        attributeValue: '',
        ordering: '',
        otpDelivery: '',
    };

    const [step, setStep] = useState(1);
    const [hasChanges, setHasChanges] = useState(false);

    const initialStates = {
        selected: null,
        selectedSection: null,
        isDropdownOpen: false,
        isConfigurationModalOpen: false,
        isConfirmModal: false,
        isUpdate: false,
        isView: false,
    };

    const [states, setStates] = useState(initialStates);

    const [form, setForm] = useState(initialForm);

    const { handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched, validateForm, touched, errors, values, dirty } = useFormik({
        initialValues: data,
        validationSchema: apiConfigurationSchema({ fromMakerChecker, isChecker }),
        onSubmit: (e) => {
            if (!viewOnly && !fromMakerChecker) {
                if (hasChanges) {
                    const { cardInquiry, otpGenerator, otpValidator } = e;

                    const processAttributeValue = (item) => ({
                        ...item,
                        attributeValue: item?.attributeValue === '' ? null : item?.attributeValue,
                        attributeField: undefined,
                        otpDelivery: undefined,
                    });

                    const updatedPayload = {
                        cardInquiry: cardInquiry.map(processAttributeValue),
                        otpValidator: otpValidator.map(processAttributeValue),
                        otpGenerator: otpGenerator.map(item => {
                            const selected = apiConfigOtpValidityOptions.find(option => option.value === item.otpDelivery);
                            return {
                                ...processAttributeValue(item),
                                otpDelivery: selected && {
                                    id: selected.value,
                                    code: selected.label
                                }
                            };
                        }),
                    };
                    handleFormSubmit(updatedPayload);
                } else {
                    toast.info('No changes found');
                }
            }
        },
    });

    const handleAcceptReject = async (isAccept) => {
        const formErrors = await validateForm();

        if (Object.keys(formErrors).length < 1) {
            const { reason, ...apiConfiguration } = values;
            const { cardInquiry, otpGenerator, otpValidator } = apiConfiguration;

            const processAttributeValue = (item) => ({
                ...item,
                attributeValue: item?.attributeValue === '' ? null : item?.attributeValue,
                attributeField: undefined,
                otpDelivery: undefined,
            });

            const updatedPayload = {
                cardInquiry: cardInquiry.map(processAttributeValue),
                otpValidator: otpValidator.map(processAttributeValue),
                otpGenerator: otpGenerator.map(item => {
                    const selected = apiConfigOtpValidityOptions.find(option => option.value === item.otpDelivery);
                    return {
                        ...processAttributeValue(item),
                        otpDelivery: selected && {
                            id: selected.value,
                            code: selected.label
                        }
                    };
                }),
            };
            handleFormSubmit({
                isaccepted: isAccept,
                apiConfiguration: updatedPayload,
                reason,
            });
        } else {
            handleSubmit();
        }
    };

    const handleSelectRow = ({ row, section }) => {
        if (row === states.selected && states.isDropdownOpen) {
            setStates((old) => ({ ...old, isDropdownOpen: false, selected: null, selectedSection: null }));
        } else {
            setStates((old) => ({ ...old, isDropdownOpen: true, selected: row, selectedSection: section }));
        }
    };

    const handleCloseDropdown = () => {
        setStates((old) => ({ ...old, isDropdownOpen: false }));
    };

    const handleConfigModalOpen = async ({ type, name, section }) => {
        if (['view', 'edit'].includes(type)) {
            // For editing, populate the form with the selected configuration data
            setForm({
                ...states.selected,
                apiName: states.selected.apiName,
                apiType: states.selected.apiType,
                attributeName: states.selected.attributeName,
                isDatetimeField: states.selected.isDatetimeField || false,
                attributeValue: states.selected.attributeValue || '',
                ordering: states.selected.ordering || '',
                otpDelivery: states.selected.otpDelivery || '',
            });

            if (type === 'view') {
                setStates((old) => ({ ...old, isUpdate: false, isView: true, selectedSection: section, isConfigurationModalOpen: true }));
            } else if (type === 'edit') {
                setStates((old) => ({ ...old, isUpdate: true, isView: false, selectedSection: section, isConfigurationModalOpen: true }));
            }
        } else {
            // For adding new configuration
            setForm({ ...initialForm, apiName: name });
            setStates((old) => ({
                ...old,
                selectedSection: section,
                isConfigurationModalOpen: true
            }));
        }
    };

    const handleConfigModalClose = () => {
        setStates((old) => ({ ...old, isConfigurationModalOpen: false, isConfirmModal: false, selected: null, selectedSection: null, isUpdate: false, isView: false }));
    };

    const handleConfigurationSubmit = (e) => {
        if (states.isUpdate) {
            // Update existing configuration
            const updatedConfigurations = values?.[states.selectedSection]?.map(config =>
                config === states.selected ? e : config
            );
            setFieldValue(states.selectedSection, updatedConfigurations);
        } else {
            // Add new configuration
            setFieldValue(states.selectedSection, [...values?.[states.selectedSection] || [], e]);
        }
        handleConfigModalClose();
    };

    const validateStep = async ({ name, step }) => {
        if (viewOnly || isChecker) {
            setStep(step + 1);
            return;
        }

        // if (values?.[name]?.length > 0) {
            await setFieldTouched(name, true, true);
        // }

        const formErrors = await validateForm();
        
        if (!formErrors?.[name]) {
            setStep(step + 1);
        } else {
            toast.error('Atleast one row is required in this step');
        }
    };

    // const handleNumberInput = (e) => {
    //     const value = e.target.value.replace(/[^0-9]/g, '');
    //     e.target.value = value;
    // };

    const actions = () => {
        const actionsList = [];

        if (isDisabledAll) {
            actionsList.push({ label: 'View', action: () => handleConfigModalOpen({ type: 'view', section: states.selectedSection }) });
        } else {
            actionsList.push({ label: 'Edit', action: () => handleConfigModalOpen({ type: 'edit', section: states.selectedSection }) });
            actionsList.push({ label: 'Remove', action: () => handleRemoveConfiguration(states.selectedSection, states.selected) });
        }

        return actionsList;
    };

    const handleRemoveConfiguration = (section, item) => {
        if (!viewOnly && !isChecker) {
            const updatedItems = values?.[section]?.filter(config => config !== item);
            setFieldValue(section, updatedItems);
            setStates((old) => ({ ...old, isDropdownOpen: false, selected: null, selectedSection: null }));
        }
    };

    const fields = [
        {
            step: 1,
            label: 'Card Inquiry',
            name: 'cardInquiry',
            sections: [
                {
                    type: 'head',
                    title: 'Card Inquiry Configuration',
                    action: { label: 'Add Configuration', onClick: () => handleConfigModalOpen({ type: 'add', name: 'card-inquiry', section: 'cardInquiry' }) },
                },
                {
                    type: 'table',
                    columns: [
                        { field: 'apiName', headerName: 'API Name', width: 100 },
                        { field: 'apiType', headerName: 'API Type', width: 100, align: 'center' },
                        { field: 'attributeName', headerName: 'Attribute Name', width: 120, align: 'center' },
                        {
                            field: 'attributeValue', headerName: 'Attribute Value', width: 100, align: 'center',
                            valueGetter: (row) => {
                                return row?.attributeValue || 'null';
                            }
                        },
                        {
                            field: 'ordering', headerName: 'Ordering', width: 100, align: 'center',
                            valueGetter: (row) => {
                                return row?.ordering || '';
                            },
                            sortBy: (row) => parseInt(row?.ordering),
                        },
                        {
                            field: 'action',
                            headerName: 'Actions',
                            sortable: false,
                            width: 100,
                            align: 'right',
                            sticky: 'right',
                            valueGetter: (row) => {
                                return (
                                    <div className="action-dropdown" style={{ position: 'relative', float: 'right' }}>
                                        <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow({ row, section: 'cardInquiry' })} />
                                        <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row} close={handleCloseDropdown} actions={actions()} />
                                    </div>
                                )
                            },
                        },
                    ]
                },
                {
                    type: 'action',
                    actions: [
                        { label: 'Cancel', type: 'button', outline: true, action: close },
                        { label: 'Next', type: 'button', action: () => validateStep({ name: 'cardInquiry', step: 1 }) }
                    ],
                }
            ]
        },
        {
            step: 2,
            label: 'Generate OTP',
            name: 'otpGenerator',
            sections: [
                {
                    type: 'head',
                    title: 'Generate OTP Configuration (Optional)',
                    action: { label: 'Add Configuration', onClick: () => handleConfigModalOpen({ type: 'add', name: 'generate-otp', section: 'otpGenerator' }) },
                },
                {
                    type: 'table',
                    columns: [
                        { field: 'apiName', headerName: 'API Name', width: 100 },
                        { field: 'apiType', headerName: 'API Type', width: 100, align: 'center' },
                        { field: 'attributeName', headerName: 'Attribute Name', width: 120, align: 'center' },
                        {
                            field: 'attributeValue', headerName: 'Attribute Value', width: 100, align: 'center',
                            valueGetter: (row) => {
                                return row?.attributeValue || 'null';
                            }
                        },
                        {
                            field: 'ordering', headerName: 'Ordering', width: 100, align: 'center',
                            valueGetter: (row) => {
                                return row?.ordering || '';
                            },
                            sortBy: (row) => parseInt(row?.ordering),
                        },
                        {
                            field: 'otpDelivery', headerName: 'OTP Delivery', width: 100, align: 'center',
                            valueGetter: (row) => {
                                const selected = apiConfigOtpValidityOptions.find(option => option.value === row.otpDelivery);
                                return selected ? selected.label : '';
                            }
                        },
                        {
                            field: 'action',
                            headerName: 'Actions',
                            sortable: false,
                            width: 100,
                            align: 'right',
                            sticky: 'right',
                            valueGetter: (row) => {
                                return (
                                    <div className="action-dropdown" style={{ position: 'relative', float: 'right' }}>
                                        <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow({ row, section: 'otpGenerator' })} />
                                        <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row} close={handleCloseDropdown} actions={actions()} />
                                    </div>
                                )
                            },
                        },
                    ]
                },
                {
                    type: 'action',
                    actions: [
                        { label: 'Back', type: 'button', outline: true, action: () => setStep(step - 1) },
                        { label: 'Next', type: 'button', action: () => validateStep({ name: 'otpGenerator', step: 2 }) }
                    ],
                }
            ],
        },
        {
            step: 3,
            label: 'OTP Validation',
            name: 'otpValidator',
            sections: [
                {
                    type: 'head',
                    title: 'OTP Validation Configuration (Optional)',
                    action: { label: 'Add Configuration', onClick: () => handleConfigModalOpen({ type: 'add', name: 'otp-validation', section: 'otpValidator' }) },
                },
                {
                    type: 'table',
                    columns: [
                        { field: 'apiName', headerName: 'API Name', width: 100 },
                        { field: 'apiType', headerName: 'API Type', width: 100, align: 'center' },
                        { field: 'attributeName', headerName: 'Attribute Name', width: 120, align: 'center' },
                        {
                            field: 'attributeValue', headerName: 'Attribute Value', width: 100, align: 'center',
                            valueGetter: (row) => {
                                return row?.attributeValue || 'null';
                            }
                        },
                        {
                            field: 'ordering', headerName: 'Ordering', width: 100, align: 'center',
                            valueGetter: (row) => {
                                return row?.ordering || '';
                            },
                            sortBy: (row) => parseInt(row?.ordering),
                        },
                        {
                            field: 'action',
                            headerName: 'Actions',
                            sortable: false,
                            width: 100,
                            align: 'right',
                            sticky: 'right',
                            valueGetter: (row) => {
                                return (
                                    <div className="action-dropdown" style={{ position: 'relative', float: 'right' }}>
                                        <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow({ row, section: 'otpValidator' })} />
                                        <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row} close={handleCloseDropdown} actions={actions()} />
                                    </div>
                                )
                            },
                        },
                    ]
                },
                ...(fromMakerChecker ? [{
                    type: 'textarea',
                    label: 'Comments',
                    placeholder: 'Enter Comments',
                    name: 'reason',
                    value: values?.reason,
                }] : []),
                {
                    type: 'action',
                    actions: fromMakerChecker ? [
                        { label: 'Back', type: 'button', outline: true, action: () => setStep(step - 1) },
                        { label: 'Reject', type: 'button', color: 'error', outline: true, action: () => handleAcceptReject(false) },
                        { label: isChecker ? 'Approve' : 'Resend', type: 'button', action: () => handleAcceptReject(true) }
                    ] : [
                        { label: 'Back', type: 'button', outline: true, action: () => setStep(step - 1) },
                        { label: viewOnly ? 'Close' : update ? 'Update' : 'Submit', type: viewOnly ? 'button' : 'submit', action: () => { viewOnly && close() } }
                    ]
                }
            ],
        }
    ];

    useEffect(() => {
        setHasChanges(dirty);
    }, [dirty]);

    return (
        <>
            <ModalComponent close={close} label={label} width={1100} slide={slide}>
                {/* MODAL BODY */}
                <Form onSubmit={handleSubmit}>
                    <div>
                        <StepsProgressBarComponent currentStep={step}
                            data={
                                fields
                                    .filter((e) => !e.hide)
                                    .map((e) => ({ step: e.step, label: e.label }))
                            }
                        />
                    </div>

                    {
                        institution &&
                        <InstitutionSection>
                            {/* <Label>Institution</Label> */}
                            <InputComponent type="text" label="Institution" value={institution?.descr} disabled mui />
                        </InstitutionSection>
                    }

                    {
                        fields && fields.length > 0 && fields.map((item, mainIndex) => (
                            <Fragment key={mainIndex}>
                                {
                                    !item.hide && item.step === step && item.sections && item.sections.length > 0 && item.sections.map((section, sectionIndex) => (
                                        <Fragment key={sectionIndex}>
                                            {/* HEAD */}
                                            {
                                                section.type === 'head' && !section.hide &&
                                                <AuthPageHead>
                                                    {
                                                        section.title &&
                                                        <div className="left">
                                                            <h5>{section.title}</h5>
                                                        </div>
                                                    }
                                                    {
                                                        section?.action?.onClick && !isDisabledAll &&
                                                        <div className="right">
                                                            <ButtonComponent icon={PlusSvg} label={section?.action?.label} size="small" onClick={section?.action?.onClick} disabled={isDisabledAll} />
                                                        </div>
                                                    }
                                                </AuthPageHead>
                                            }

                                            {/* TABLE */}
                                            {
                                                section.type === 'table' && !section.hide &&
                                                <TableWrapper>
                                                    {
                                                        values?.[item?.name] && values?.[item?.name].length > 0 ? (
                                                            <TableComponent rows={values?.[item?.name]} columns={section.columns} icon={ListSettingSvg} />
                                                        ) : (
                                                            <EmptyModalComponent text='No configuration found!' />
                                                        )
                                                    }
                                                    {
                                                        touched[item?.name] && errors[item?.name] && (
                                                            <FormError>{errors[item?.name]}</FormError>
                                                        )
                                                    }
                                                </TableWrapper>
                                            }

                                            {
                                                section.type === 'textarea' && !section.hide &&
                                                <div>
                                                    <Label>Comments</Label>
                                                    <TextareaComponent
                                                        placeholder={section.placeholder}
                                                        onBlur={handleBlur}
                                                        name={section.name}
                                                        value={section.value}
                                                        onChange={handleChange}
                                                        disabled={section.disabled}
                                                    />
                                                    {
                                                        touched[section.name] && errors[section.name] && (
                                                            <FormError>{errors[section.name]}</FormError>
                                                        )
                                                    }
                                                </div>
                                            }

                                            {/* ACTIONS */}
                                            {
                                                section.type === 'action' && section.actions.length > 0 && !section.hide &&
                                                <ActionsContainer>
                                                    {
                                                        section.actions.map((button, index) => (
                                                            <ButtonComponent key={index} label={button.label} type={button.type} outline={button.outline} color={button?.color} onClick={button.action} />
                                                        ))
                                                    }
                                                </ActionsContainer>
                                            }
                                        </Fragment>
                                    ))
                                }
                            </Fragment>
                        ))
                    }
                </Form>
            </ModalComponent>
            {
                states?.isConfigurationModalOpen &&
                <AddUpdateApiConfigurationFieldsModal data={form} label={`${states?.isUpdate ? 'Update' : states?.isView ? 'View' : 'Add New'} Configuration`} step={step} parent={values[states?.selectedSection]} update={states.isUpdate} viewOnly={states.isView} close={handleConfigModalClose} handleConfigurationSubmit={handleConfigurationSubmit} slide/>
            }
        </>
    );
};

export default AddUpdateApiConfigurationModal;